/**
 * Centralized validation for Create/Edit User forms.
 * Validation runs only on submit; Radiologist/Technician follow user-story rules in `userFormFieldRules`.
 */

import {
  USER_MSG,
  validateUserDobStory,
  validateEmployeeIdStory,
  validateFullNameStory,
  validateGenderStory,
  validateEmailStory,
  validatePhoneStoryDigits,
  validateUsernameStory,
  validateUserPasswordStory,
  validateStatusStoryDialog,
  validateOptionalExperienceYears,
} from '../validation/userFormFieldRules';

export type CreateUserFormState = Record<string, string>;

const MESSAGES = {
  shiftStartAt: 'Shift start time is required',
  shiftEndAt: 'Shift end time is required',
  subRole: 'Sub Role is required',
  registrationNumber: 'Registration No. is required',
  qualification: 'Qualification is required',
  modalityAccess: 'At least one modality is required',
  dateOfBirthFormat: 'Date must be dd/mm/yyyy (day 2 digits, month 2 digits, year 4 digits)',
  dateOfBirth: 'Date of Birth is required',
} as const;

function trim(s: string | undefined): string {
  return (s ?? '').trim();
}

function getMobileDigits(raw: string | undefined): string {
  if (raw == null) {
    return '';
  }
  return String(raw).trim().replace(/\D/g, '').slice(-10);
}

function isValidYmdParts(parts: string[]): boolean {
  if (parts.length !== 3 || parts[0].length !== 4 || parts[1].length > 2 || parts[2].length > 2) {
    return false;
  }
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  return (
    !isNaN(year) &&
    !isNaN(month) &&
    !isNaN(day) &&
    day >= 1 &&
    day <= 31 &&
    month >= 1 &&
    month <= 12 &&
    year >= 1900 &&
    year <= 2100
  );
}

function isValidDmyParts(parts: string[]): boolean {
  if (parts.length !== 3) {
    return false;
  }
  const [dd, mm, yyyy] = parts;
  if (dd.length !== 2 || mm.length !== 2 || yyyy.length !== 4) {
    return false;
  }
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  const year = parseInt(yyyy, 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return false;
  }
  if (day < 1 || day > 31) {
    return false;
  }
  if (month < 1 || month > 12) {
    return false;
  }
  if (year < 1900 || year > 2100) {
    return false;
  }
  return true;
}

/** Validate date: accepts yyyy-mm-dd (date input) or dd/mm/yyyy. Referring Physician legacy format check. */
export function validateDateOfBirthFormat(dob: string | undefined): string {
  const s = trim(dob);
  if (!s) {
    return MESSAGES.dateOfBirth;
  }
  if (s.includes('-')) {
    return isValidYmdParts(s.split('-')) ? '' : MESSAGES.dateOfBirthFormat;
  }
  return isValidDmyParts(s.split('/').map(p => p.trim())) ? '' : MESSAGES.dateOfBirthFormat;
}

export interface ValidationOptions {
  /** When false, sub-role is not required for Radiologist. Default true. */
  requireSubRole?: boolean;
}

function applyTechnicianShiftErrors(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>
): void {
  if (!trim(form.shiftStartAt)) {
    errors.shiftStartAt = MESSAGES.shiftStartAt;
  }
  if (!trim(form.shiftEndAt)) {
    errors.shiftEndAt = MESSAGES.shiftEndAt;
  }
}

function applyRadiologistRoleErrors(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>,
  requireSubRole: boolean
): void {
  if (requireSubRole && !trim(form.subRole)) {
    errors.subRole = MESSAGES.subRole;
  }
  if (!trim(form.registrationNumber)) {
    errors.registrationNumber = MESSAGES.registrationNumber;
  }
  if (!trim(form.qualification)) {
    errors.qualification = MESSAGES.qualification;
  }
  const modalities = (form.modalityAccess ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (modalities.length === 0) {
    errors.modalityAccess = MESSAGES.modalityAccess;
  }
  applyTechnicianShiftErrors(form, errors);
}

function applyCommonStoryFieldErrors(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>,
  mode: 'create' | 'edit'
): void {
  const eid = validateEmployeeIdStory(form.employeeId);
  if (eid) {
    errors.employeeId = eid;
  }

  const fn = validateFullNameStory(form.fullName);
  if (fn) {
    errors.fullName = fn;
  }

  const g = validateGenderStory(form.gender);
  if (g) {
    errors.gender = g;
  }

  const dobErr = validateUserDobStory(form.dateOfBirth);
  if (dobErr) {
    errors.dateOfBirth = dobErr;
  }

  const em = validateEmailStory(form.email);
  if (em) {
    errors.email = em;
  }

  const phoneErr = validatePhoneStoryDigits(getMobileDigits(form.mobileNumber));
  if (phoneErr) {
    errors.mobileNumber = phoneErr;
  }

  const un = validateUsernameStory(form.username);
  if (un) {
    errors.username = un;
  }

  if (mode === 'create') {
    const pwd = validateUserPasswordStory(form.password, form.username, form.email);
    if (pwd) {
      errors.password = pwd;
    }
  }

  const st = validateStatusStoryDialog(form.status);
  if (st) {
    errors.status = st;
  }

  const expErr = validateOptionalExperienceYears(form.experience);
  if (expErr) {
    errors.experience = expErr;
  }
}

function applyRadiologistTechnicianStoryWithOptions(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>,
  mode: 'create' | 'edit',
  options?: ValidationOptions
): void {
  const { requireSubRole = true } = options ?? {};

  if (!trim(form.role)) {
    errors.role = USER_MSG.role;
    return;
  }
  if (form.role !== 'Radiologist' && form.role !== 'Technician') {
    return;
  }

  applyCommonStoryFieldErrors(form, errors, mode);

  if (form.role === 'Technician') {
    applyTechnicianShiftErrors(form, errors);
  }

  if (form.role === 'Radiologist') {
    applyRadiologistRoleErrors(form, errors, requireSubRole);
  }
}

function applyReferringPhysicianFullNameError(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>
): void {
  const fullNameTrimmed = trim(form.fullName);
  if (!fullNameTrimmed) {
    errors.fullName = 'Full Name is required';
  } else if (fullNameTrimmed.length < 3) {
    errors.fullName = 'Full Name must be at least 3 characters';
  } else if (!/^[a-zA-Z\s]+$/.test(fullNameTrimmed)) {
    errors.fullName = 'Full Name must contain only alphabets';
  }
}

function applyReferringPhysicianSharedErrors(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>,
  includePassword: boolean
): void {
  applyReferringPhysicianFullNameError(form, errors);

  const emailErrRp = validateEmailStory(form.email);
  if (emailErrRp) {
    errors.email = emailErrRp;
  }
  const phoneErr = validatePhoneStoryDigits(getMobileDigits(form.mobileNumber));
  if (phoneErr) {
    errors.mobileNumber = phoneErr;
  }
  if (!trim(form.gender)) {
    errors.gender = 'Gender is required';
  }
  const dobError = validateDateOfBirthFormat(form.dateOfBirth);
  if (dobError) {
    errors.dateOfBirth = dobError;
  }
  if (!trim(form.username)) {
    errors.username = 'Username is required';
  }
  if (includePassword && !trim(form.password)) {
    errors.password = 'Password is required';
  }
  if (!trim(form.status)) {
    errors.status = 'Status is required';
  }
  const qualificationTrimmed = trim(form.qualification);
  if (qualificationTrimmed && !/^[a-zA-Z0-9\s]+$/.test(qualificationTrimmed)) {
    errors.qualification = 'Qualification must be alphanumeric';
  }
}

function applyOtherRoleSharedErrors(
  form: CreateUserFormState,
  errors: Partial<Record<string, string>>,
  includePassword: boolean
): void {
  if (form.role !== 'ReferringPhysician' && !trim(form.employeeId)) {
    errors.employeeId = 'Employee ID is required';
  }
  if (!trim(form.fullName)) {
    errors.fullName = 'Full Name is required';
  }
  if (!trim(form.gender)) {
    errors.gender = 'Gender is required';
  }
  const dobError = validateDateOfBirthFormat(form.dateOfBirth);
  if (dobError) {
    errors.dateOfBirth = dobError;
  }
  const emailErr = validateEmailStory(form.email);
  if (emailErr) {
    errors.email = emailErr;
  }
  const mobileDigits = getMobileDigits(form.mobileNumber);
  if (mobileDigits.length !== 10) {
    errors.mobileNumber = 'Phone No. is required';
  }
  if (!trim(form.username)) {
    errors.username = 'Username is required';
  }
  if (includePassword && !trim(form.password)) {
    errors.password = 'Password is required';
  }
  if (!trim(form.status)) {
    errors.status = 'Status is required';
  }
}

/**
 * Returns validation errors for Create User form. Call only on submit.
 */
export function getCreateUserValidationErrors(
  form: CreateUserFormState,
  options?: ValidationOptions
): Partial<Record<keyof CreateUserFormState, string>> {
  const errors: Partial<Record<string, string>> = {};

  if (!trim(form.role)) {
    return { role: USER_MSG.role };
  }

  if (form.role === 'ReferringPhysician') {
    applyReferringPhysicianSharedErrors(form, errors, true);
    return errors;
  }

  applyRadiologistTechnicianStoryWithOptions(form, errors, 'create', options);

  if (form.role === 'Radiologist' || form.role === 'Technician') {
    return errors;
  }

  applyOtherRoleSharedErrors(form, errors, true);
  return errors;
}

/**
 * Returns validation errors for Edit User form. Call only on submit.
 */
export function getEditUserValidationErrors(
  form: CreateUserFormState,
  options?: ValidationOptions
): Partial<Record<keyof CreateUserFormState, string>> {
  const errors: Partial<Record<string, string>> = {};

  if (!trim(form.role)) {
    return { role: USER_MSG.role };
  }

  if (form.role === 'ReferringPhysician') {
    applyReferringPhysicianSharedErrors(form, errors, false);
    return errors;
  }

  applyRadiologistTechnicianStoryWithOptions(form, errors, 'edit', options);

  if (form.role === 'Radiologist' || form.role === 'Technician') {
    return errors;
  }

  applyOtherRoleSharedErrors(form, errors, false);
  return errors;
}
